import { AutoAttrTracker } from '@byted/attr-tracker-sdk';

import Cookie from 'js-cookie';
import { Collector } from '@dp/byted-tea-sdk-oversea';
import { isBOE } from 'src/const/boe';
import uuidv4 from 'uuid/v4';
import qs from 'qs';

const eventUuid = uuidv4();

const id = (() => {
  try {
    const { creation_uuid } = qs.parse(window.location.search, { ignoreQueryPrefix: true });
    return creation_uuid;
  } catch (e) {
    return '';
  }
})();

// 账户状态
const STATE_MAP = {
  0: 'login_undefined',
  1: 'internal_super',
  2: 'internal_other',
  3: 'external',
  99: 'not_log_in',
};
// 事件类型对应（点击时刻和有值时刻）
const EVENT_MAP = {
  click: 'click',
  focus: 'click',
  blur: 'content',
};

class TeaTracker {
  Tracker;

  ABTracker;

  attrLog;

  baseConfig;

  loginInfo;

  advInfo;

  isStart;

  queuedEventsBeforeLogin = [];

  constructor() {
    this.Tracker = new Collector('tracker');
    this.ABTracker = new Collector('abTracker');
    this.DataFinder = new Collector('dataFinder');
    this.baseConfig = {
      app_id: 1583,
      log: false,
      channel: 'sg',
      _staging_flag: process.env.NODE_ENV !== 'production' || isBOE ? 1 : 0,
      disable_ssid: false,
      disable_webid: false,
      disable_sdk_monitor: false,
      disable_storage: false,
      disable_auto_pv: true, // 是否禁止SDK默认上报predefine_pageview事件
      autotrack: { // 对内的无埋点，必须填以下的值
        custom: 'tea', // 固定值
      },
      reportTime: 500,
    };
    this.isStart = false;
    this._init();
  }

  config(options) {
    this.setTrackConfig(options);
    this.setABTrackConfig(options);
    this.setDataFinderConfig(options);
  }

  start() {
    if (!this.isStart) {
      this.Tracker.start();
      this.ABTracker.start();
      this.DataFinder.start();
      this.isStart = true;
    }
  }

  setTrackConfig(options) {
    this.Tracker.config(options);
  }

  setABTrackConfig(options) {
    this.ABTracker.config(options);
  }

  setDataFinderConfig(options) {
    this.DataFinder.config(options);
  }

  trackEvent(eventName, params) {
    const info = this.loginInfo?.account;
    const coreUser = this.loginInfo?.core_user;
    this.Tracker.event(eventName, {
      ...params,
      adv_id: String(info?.id ?? 0),
      core_user_id: (coreUser?.core_user_id ?? ''),
    });
    this.ABTracker.event(eventName, {
      ...params,
      adv_id: String(info?.id ?? 0),
      core_user_id: (coreUser?.core_user_id ?? ''),
    });
  }

  // DataFinder 使用独立的事件上报方法
  reportEventToDataFinder(eventName, params) {
    this.DataFinder(eventName, params);
  }

  trackView(options) {
    this.Tracker.predefinePageView(options);
    this.ABTracker.predefinePageView(options);
    this.DataFinder.predefinePageView(options);
  }

  setLoginInfo(info, advInfo) {
    this.loginInfo = info;
    this.advInfo = advInfo || {};
    const user = this.getUserParams();
    const adv = this.getAdvParams();
    const bases = this.getBaseConfig();
    window.creation_uuid = id || eventUuid;
    this.config({
      ...user,
      ...adv,
      ...bases,
      log_in: this.isLogin(),
      user_type_name: STATE_MAP[user.user_type],
      uiVersion: 'uirefresh',
      webid: Cookie.get('tt_webid') || '',
      creation_uuid: window.creation_uuid,
    });

    this.setABTrackConfig({
      user_id: (adv.adv_id),
      user_unique_id: adv.adv_id,
    });

    if (this.isLogin()) {
      this.queuedEventsBeforeLogin.forEach(fn => fn());
      this.queuedEventsBeforeLogin = [];
    }
  }

  getUserParams() {
    const { core_user: coreUser, coreUserInfo } = this.loginInfo;
    const { staff } = this.loginInfo;
    const { account } = this.loginInfo;
    let user_type = 99;
    if (this.isAdmin()) {
      user_type = 1;
    } else if (staff?.staff_id) {
      user_type = 2;
    } else if (account?.id) {
      user_type = 3;
    } else if (this.isLogin()) {
      user_type = 0;
    }

    const { first_industry, second_industry } = this.advInfo || {};

    return {
      user_id: (coreUser?.core_user_id ?? coreUserInfo?.user_id_str ?? ''),
      user_unique_id: String(coreUser?.core_user_id ?? coreUserInfo?.user_id_str ?? ''),
      user_mail: this.loginInfo.email ?? coreUserInfo?.email ?? '',
      user_type,
      user_name: coreUser?.name ?? coreUserInfo?.username ?? '',
      first_industry: first_industry || '',
      second_industry: second_industry || '',
    };
  }

  getAdvParams() {
    const info = this.loginInfo.account;
    const advParams = {
      adv_id: String(info?.id ?? 0),
      adv_email: this.loginInfo?.email ?? '',
      adv_role: info?.role ?? 0,
      adv_name: info?.name ?? '',
      customer_type_value: info?.customer_type_value ?? 0,
      project_version: '0',
    };
    return advParams;
  }

  getBaseConfig() {
    return {
      lang: Cookie.get('lang_type') || 'en',
      app_timezone: this.loginInfo?.account?.timezone ?? '',
      app_region: String(this.loginInfo?.account?.country ?? '').toUpperCase(),
    };
  }

  isAdmin() {
    return Boolean(this?.loginInfo?.permission?.is_admin);
  }

  isLogin() {
    const { loginInfo } = this;
    const hasAccountNameAndId = Boolean(loginInfo?.account?.name && loginInfo?.account?.id);
    return hasAccountNameAndId;
  }

  _init() {
    this.Tracker.init(this.baseConfig);
    this.ABTracker.init({
      ...this.baseConfig,
      app_id: 4697,
    });
    this.DataFinder.init({
      ...this.baseConfig,
      app_id: 224013,
      channel_domain: 'https://sgali-mcs.byteoversea.com',
      autotrack: true,
    });
    this.config({ disable_auto_pv: true /** 是否禁止SDK默认上报predefine_pageview事件 */ });
    this.start();
  }
}

const TeaInstance = new TeaTracker();

TeaInstance.config({
  app_language: Cookie.get('lang_type') || 'en',
  app_name: 'TikTok Ads',
});


TeaInstance.attrLog = new AutoAttrTracker({
  allowEventTypes: ['click', 'focus', 'blur', 'expose'], // [可选]事件代理监听的事件类型 ， 默认 ['click','expose']
  debounceWaitTime: 100, // [可选] 事件触发到打点的等待时间, 函数防抖，避免短时间重复埋点
  exposeTime: 2000, // [可选] 模块曝光 =》到打点的等待时间， 默认： 1000ms
  exposePercent: 0.5, // [可选] 模块曝光的比例 1=100% , 0.5=50% ， 默认：0.5
  refreshObserverTime: 2000, // [可选] 间隔一段时间，重新建通模块曝光

  /**
   * 通过DOM属性触发的事件埋点处理函数
   * @param {string} eventName 事件名 data-tea-[eventType] 属性的值
   * @param {Object.<string, unknown>} eventParams 事件参数，可以通过添加 data-tea-[key]="[value]" 属性来设置事件参数
   * @param {"click" | "focus" | "blur" | "expose"} eventType 事件类型 click / focus / blur / expose
   */
  log: (eventName, eventParams, eventType) => {
    if (eventName) {
      if (EVENT_MAP[eventType]) {
        eventParams.create_flow_event_time_type = EVENT_MAP[eventType];
      }
      TeaInstance.trackEvent(eventName, eventParams);
    }
  },
});

const TeaPlugin = {
  tracker: TeaInstance,
  installed: false,

  install(Vue, initConf = {}) {
    const { tracker } = this;

    if (!TeaPlugin.installed) {
      TeaPlugin.installed = true;
    } else {
      return;
    }
    const vueRouter = initConf.vueRouter || null;
    let ignoredViews = initConf.ignoredViews || [];

    let fromUrl = '';
    const initRouterGuard = function () {
      // 忽略的url
      if (!vueRouter) {
        return;
      }
      if (ignoredViews) {
        ignoredViews = ignoredViews.map(view => view.toLowerCase());
      }
      vueRouter.afterEach((to, from) => {
        if (isBOE) return;
        fromUrl = window.location.origin + from.fullPath;
        if (ignoredViews && ignoredViews.indexOf((to.name || '').toLowerCase()) !== -1) {
          return;
        } // 这里不需要每次路由之后都初始化tea，，，只需要在路由之后发pv就行了

        const options = {
          url: window.location.origin + to.fullPath,
          url_path: to.path,
          title: to.name,
          from_url: fromUrl,
          from_name: from.name,
        };


        if (tracker.isLogin()) {
          TeaInstance.trackView(options);
        } else {
          tracker.queuedEventsBeforeLogin.push(() => TeaInstance.trackView(options));
        }
      });

      return ignoredViews;
    };

    initRouterGuard();

    Vue.prototype.trackEvent = TeaInstance.trackEvent.bind(TeaInstance);
    Vue.prototype.trackView = TeaInstance.trackView.bind(TeaInstance);
    Vue.prototype.$tea = TeaInstance;
  },
};

export default TeaPlugin;
